import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AppWindow, ArrowUpRight, Bot, Mail, MessageSquareText, Plus, Smartphone, X } from 'lucide-react';
import { Modal } from '../../Modal';
import { connectorLogoUrl } from '../../../lib/connectors';
import type { Home3ReadState } from '../../../lib/home3-feed';
import * as L from '../../../lib/home-map/phone-layout';
import { phoneCardPlacement, type PhoneCardAnchorMode } from '../../../lib/home-map/phone-popover';
import type { PhoneMapCardContent } from '../../../lib/home-map/phone-map-cards';
import { handleModalEscape, pushModalEscape } from '../../../lib/modal-escape';
import { useTranslation } from '../../../lib/i18n';
import { WhatsAppMapGlyph, WixWordmark } from '../connectors/BrandGlyphs';
import { Button, Card, IconButton, Typography } from '../ui';
import { Home3Summary } from './Home3Summary';
import styles from './Home3PhoneMapCards.module.css';

/**
 * The card a dot or a chip opens over the phone activity map — the agent card (Figma 2048:122213), the
 * connector card (Figma 2048:125261), the folded card a tray's "+N" opens and the "Connect more" card the tools
 * tray's "+" opens are ONE layout: a mark beside a bold
 * name (an agent adds its role under them), a hairline, the description (the folded card: its list, `children`),
 * an outlined full-width action, and ✕. Metrics are `phone-layout.ts` `mapCard*`; what it says is
 * `lib/home-map/phone-map-cards.ts`. Mirrors iOS/Android `ActivityMapCard`.
 *
 * ## Next to what was pressed, and a press outside closes it
 *
 * Every popover on the map opens NEXT TO its trigger (`anchor`; user decision 2026-11-03, superseding the centred
 * card): beside an agent's dot or the Maestro, above — else below — a tray chip, a "+N" or the "+", by the rule in
 * `lib/home-map/phone-popover.ts`, inside the screen's 20 side gutters and the band. The trigger is measured on screen
 * (`getBoundingClientRect`), so the card follows the page's scroll and the board's pinch zoom; it is measured again on
 * a scroll or a resize. With no anchor it is centred. It is closed by ✕, Escape or a press anywhere outside. The
 * frames draw no scrim, so the layer under the card is a TRANSPARENT catcher: it paints nothing and takes the press,
 * so a press beside the card closes it instead of acting on the map underneath.
 *
 * **Not a `Modal`** — no scrim, no page take-over, as the frames draw it (`modal-usage`'s NOT_A_MODAL). It is a
 * `role="dialog"` that takes focus when it opens and gives it back when it closes, and it claims Escape through
 * the shared modal-escape stack (`lib/modal-escape.ts`), so a panel underneath does not also close on the key.
 * Portalled to `<body>`: the map's board is CSS-scaled, and a transform makes `position: fixed` relative to it.
 */
export function PhoneMapCardDialog({
  anchor,
  mark,
  content,
  onClose,
  testId,
  onAction,
  onLink,
  children,
}: {
  /** What opened it, and how the card stands to it; `band` bounds it vertically. Without one, the card is centred. */
  anchor?: { trigger: HTMLElement; mode: PhoneCardAnchorMode; band: HTMLElement | null };
  /** Beside the title; the folded card has none — its title, "{count} more", says it. */
  mark?: ReactNode;
  content: PhoneMapCardContent;
  onClose: () => void;
  testId: string;
  /** The action was pressed, on its way out (BI); the card then closes. */
  onAction?: () => void;
  /** The link under the action ("View site") was pressed, on its way out (BI); the card then closes. */
  onLink?: () => void;
  /** What sits under the hairline in place of a description — the folded card's list. */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  // Where the card goes: measured once it has laid out (before the first paint), again on a scroll or a resize.
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  const trigger = anchor?.trigger;
  const mode = anchor?.mode;
  const band = anchor?.band;
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!trigger || !mode || !card) return undefined;
    const measure = () => {
      const bandRect = band?.getBoundingClientRect();
      const viewH = window.innerHeight;
      setPlace(
        phoneCardPlacement({
          trigger: trigger.getBoundingClientRect(),
          mode,
          card: { width: card.offsetWidth, height: card.offsetHeight },
          screenWidth: document.documentElement.clientWidth || window.innerWidth,
          // The part of the band on screen.
          band: { top: Math.max(0, bandRect?.top ?? 0), bottom: Math.min(viewH, bandRect?.bottom ?? viewH) },
        }),
      );
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    // Any change to the card's own size re-places it — content that lands after it opened (a site's subtitle, lines,
    // a link) can make it taller, and a card placed on its shorter height could run past the band.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(card);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      ro?.disconnect();
    };
  }, [trigger, mode, band]);
  const anchored = !!trigger;
  const placeStyle: CSSProperties | undefined = anchored
    ? place
      ? { left: place.left, top: place.top }
      : { left: 0, top: 0, visibility: 'hidden' }
    : undefined;

  // Escape belongs to the topmost layer: this card, while it is open.
  useEffect(() => {
    const pop = pushModalEscape(() => close.current());
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      pop();
    };
  }, []);

  // Focus moves into the card when it opens and back to what opened it when it closes.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cardRef.current?.focus({ preventScroll: true });
    return () => {
      if (opener && opener.isConnected) opener.focus();
    };
  }, []);

  // The action leaves: it reports itself (the folded card's BI), then the card closes on the way out.
  const act = () => {
    onAction?.();
    onClose();
  };

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className={styles.layer} data-testid={testId}>
      {/* The transparent catcher: a press anywhere outside the card is the card's to close. */}
      <div className={styles.catcher} onClick={onClose} aria-hidden data-card-catcher />
      <div
        ref={cardRef}
        className={`${styles.dialog}${anchored ? ` ${styles.dialogAnchored}` : ''}`}
        style={placeStyle}
        role="dialog"
        aria-label={content.title}
        tabIndex={-1}
      >
        <Card variant="sunken" disablePadding className={styles.card}>
          <div className={styles.head}>
            <span className={styles.titleRow}>
              {mark}
              <Typography variant="h2" color="primary" component="span" className={styles.title} noWrap dir="auto">
                {content.title}
              </Typography>
            </span>
            {content.subtitle && (
              <Typography variant="small-medium" color="secondary" component="span" noWrap dir="auto">
                {content.subtitle}
              </Typography>
            )}
          </div>
          <span className={styles.divider} aria-hidden />
          {content.description && (
            <Typography variant="heading-04" color="primary" component="p" className={styles.description} dir="auto">
              {content.description}
            </Typography>
          )}
          {children}
          {/* One line is the Wix site's address, which may take two; several are the other sites, one line each. */}
          {content.lines?.map((line, _, all) => (
            <Typography
              key={line}
              variant="p3"
              color="secondary"
              component="p"
              noWrap={all.length > 1}
              className={`${styles.line}${all.length > 1 ? '' : ` ${styles.lineWrap}`}`}
              dir="auto"
            >
              {line}
            </Typography>
          ))}
          {/* A scheme URL (Send SMS's `sms:`) leaves the app: a plain anchor, never the app router's mount-prefixed route. */}
          <Button
            variant="outlined"
            size="medium"
            fullWidth
            href={content.href}
            external={content.href ? /^[a-z][a-z0-9+.-]*:/i.test(content.href) : undefined}
            onClick={act}
            className={styles.action}
          >
            {content.actionLabel}
          </Button>
          {/* "View site" leaves the app for the site itself, in a new tab — the desktop card's link. */}
          {content.link && (
            <a
              href={content.link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
              onClick={() => {
                onLink?.();
                onClose();
              }}
              data-testid="phone-map-card-link"
            >
              {content.link.label}
              <ArrowUpRight size={14} aria-hidden />
            </a>
          )}
          <span className={styles.close}>
            <IconButton size="large" aria-label={t('common.close')} onClick={onClose}>
              <X size={L.mapCardCloseIcon} aria-hidden />
            </IconButton>
          </span>
        </Card>
      </div>
    </div>,
    document.body,
  );
}

/** The Brief card's width (Figma 2045:108622): the frame's 310, not the screen less its margins. */
const BRIEF_CARD_W = 310;

/**
 * The Brief rail's card (Figma 2045:108622; user decision 2026-11-03): only a 24 ✕ at its top end, 12 over the
 * day's headline, who met, "Read the meeting summary" and the read-along — the desktop canvas's brief popover CONTENT
 * (`Home3Summary` in its `popover` form, the same four states and the same actions) set to the phone frame
 * (`phoneBrief`), in the DS modal shell, kept a centred 310 card on a phone (`fullScreenOnSmall` off) over the
 * shell's blurred scrim. The shell's own ✕ is off (`hideClose`): the frame's ✕ is the card's head row, a 24 glyph on
 * the DS ghost IconButton (42, its largest box) whose hit area is grown to 44. Its insets are the frame's 20 / 20 / 30 / 24; the corner 16 and the elevated surface are the shell's.
 * A press outside, ✕ or Escape closes it; an action that leaves closes it too.
 */
export function PhoneMapBriefModal({
  readState,
  closed,
  moving,
  onClose,
}: {
  readState: Home3ReadState;
  closed: number;
  moving: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      width={BRIEF_CARD_W}
      fullScreenOnSmall={false}
      maxHeightPx={650}
      scrollBody
      hideClose
      contentClassName={styles.briefContent}
    >
      <div className={styles.briefHead}>
        <IconButton size="large" className={styles.briefClose} aria-label={t('common.close')} onClick={onClose}>
          <X size={24} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
        </IconButton>
      </div>
      <Home3Summary readState={readState} closed={closed} moving={moving} popover phoneBrief onLeave={onClose} />
    </Modal>
  );
}

/** A connector's mark: its glyph or logo in the tray's own round chip, at `size`. */
export function ChipMark({ cluster, chip, size }: { cluster: L.Cluster; chip: { slug: string; label: string; logoUrl?: string }; size: number }) {
  return (
    // The Wix chip is white with the black wordmark in both themes (the user's call): it never takes the tray's dark chip.
    <span className={`${styles.chipMark}${chip.slug === 'wix' ? ` ${styles.chipWix}` : ''}`} style={{ inlineSize: size, blockSize: size }}>
      {cluster === 'channels' ? <ChannelGlyph slug={chip.slug} label={chip.label} /> : <ToolLogo chip={chip} />}
    </span>
  );
}

/**
 * The "+" mark: the tools tray's own "+" (Fill(buttons)/Light — the white round plate, Figma 2048:123056 — with a
 * 16 Plus glyph), at `size`: the tray's `chip` 32 and the card's `mapCardChip` 28, so the "Connect more" card opens
 * on the very mark that was pressed (the desktop canvas's `profile.plus` plate).
 */
export function PlusPlate({ size }: { size: number }) {
  return (
    <span className={`${styles.chipMark} ${styles.plusPlate}`} style={{ inlineSize: size, blockSize: size }} aria-hidden>
      <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth />
    </span>
  );
}

/**
 * The folded card's list (the desktop canvas's folded card, `MapInfoCard`'s `profile.items`): each hidden chip's
 * own mark in a 28 round chip, 8 before its name in the description's type, one line; rows 8 apart. Past
 * `mapCardListMaxRows` the last row is "{n} more" in the secondary ink, with no mark. Rows are not controls.
 */
export function FoldedList({
  cluster,
  rows,
  more,
}: {
  cluster: L.Cluster;
  rows: { slug: string; label: string; logoUrl?: string }[];
  more?: string;
}) {
  return (
    <ul className={styles.list} style={{ gap: L.mapCardListRowGap }}>
      {rows.map((chip) => (
        <li key={chip.slug} className={styles.listRow} style={{ gap: L.mapCardListNameGap }}>
          <ChipMark cluster={cluster} chip={chip} size={L.mapCardChip} />
          <Typography variant="heading-04" color="primary" component="span" className={styles.listName} noWrap dir="auto">
            {chip.label}
          </Typography>
        </li>
      ))}
      {more && (
        <li className={styles.listRow}>
          <Typography variant="p2" color="secondary" component="span" className={styles.listName} noWrap>
            {more}
          </Typography>
        </li>
      )}
    </ul>
  );
}

/** The phone frame's channel glyphs, one ink: the DS WhatsApp glyph, lucide at 16 with a 1.5 stroke for the rest (Figma's
 *  `16/message-square-text`, `16/smartphone`, …). */
const CHANNEL_GLYPHS: Record<string, typeof Mail> = {
  'phone-app': Smartphone,
  web: AppWindow,
  'email-in': Mail,
  'sms-in': MessageSquareText,
};

export function ChannelGlyph({ slug, label }: { slug: string; label: string }) {
  // Figma's own DS WhatsApp glyph in its 24 box (frames 2046:117940 / 2048:123852), not the classic logo.
  if (slug === 'whatsapp') return <WhatsAppMapGlyph />;
  const Icon = CHANNEL_GLYPHS[slug] ?? (slug.startsWith('coder:') ? Bot : null);
  if (Icon) return <Icon size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />;
  return <Initial label={label} />;
}

/** A connected tool keeps its real brand logo — there the logo IS how you recognise it. An MCP server, or a logo
 *  that never loads, shows its initial; Wix its wordmark. */
export function ToolLogo({ chip }: { chip: { slug: string; label: string; logoUrl?: string } }) {
  const [failed, setFailed] = useState(false);
  if (chip.slug === 'wix') return <WixWordmark width={20} />;
  const logo = chip.slug.startsWith('mcp:') ? null : chip.logoUrl ?? connectorLogoUrl(chip.slug);
  if (!logo || failed) return <Initial label={chip.label} />;
  return <img src={logo} alt="" width={18} height={18} className={styles.logo} onError={() => setFailed(true)} />;
}

/** A chip with no mark of its own wears its name's initial. */
function Initial({ label }: { label: string }) {
  return (
    <Typography variant="tiny-semi-bold" color="primary" component="span" aria-hidden>
      {label.charAt(0).toUpperCase()}
    </Typography>
  );
}
